import { Either, Left } from 'purify-ts/Either';
import { EitherAsync } from 'purify-ts/EitherAsync';
import { Maybe } from 'purify-ts/Maybe';
import { getStoreBuilder } from 'vuex-typex';
import * as api from '@codegrade/api';
import client from '@/client';
import * as models from '@/models';
import { store } from '@/store';
import * as utils from '@/utils';
import { eitherAsyncUtils } from '@/utils';
import { AssignmentsStore } from './assignments';
import { FileTreeStore } from './file_trees';
import { SiteSettingsStore } from './siteSettings';
import { SubmissionsStore } from './submissions';
const storeBuilder = getStoreBuilder();
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
const makeInitialState = () => ({
    tests: {},
    testsByAssignment: {},
    resultByRun: {},
    runs: {},
    loaders: {
        testsByAssignmentId: {},
        results: {},
        runs: {},
        resultsBySubmissionId: {},
    },
});
const moduleBuilder = storeBuilder.module('autotest', makeInitialState());
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
const forceLoadSubs = (assignmentId) => SubmissionsStore.loadSubmissions({
    assignmentId,
    force: true,
});
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
const forceLoadRubric = (assignmentId) => EitherAsync(() => store.dispatch('rubrics/loadRubric', { assignmentId, force: true }, { root: true }));
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export var AutoTestStore;
(function (AutoTestStore) {
    /* eslint-disable-next-line no-underscore-dangle, jsdoc/require-jsdoc -- When touching please add documentation. */
    function _stateGetter(key) {
        return (state) => utils.getItem(state[key]);
    }
    // eslint-disable-next-line no-underscore-dangle
    const _getTest = _stateGetter('tests');
    // eslint-disable-next-line no-underscore-dangle
    const _getTestByAssignment = _stateGetter('testsByAssignment');
    // eslint-disable-next-line no-underscore-dangle
    const _getRun = _stateGetter('runs');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.getRun = moduleBuilder.read(_getRun, 'getRun');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.getTest = moduleBuilder.read(_getTest, 'getTest');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.getLatestResults = moduleBuilder.read(state => {
        const getter = utils.getItem(state.resultByRun);
        return (test) => test.run
            .chain(run => getter(run.id))
            .map(resultByRun => {
            const latestSubs = SubmissionsStore.getLatestSubmissions()(test.assignmentId).orDefault([]);
            return utils.filterMap(latestSubs, ({ id }) => utils
                .getItem(resultByRun.bySubmission, id)
                .chain(utils.getItem(resultByRun.all)));
        });
    }, 'getLatestResults');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.getResultById = moduleBuilder.read(state => {
        const getter = utils.getItem(state.resultByRun);
        return (runId, resultId) => getter(runId).chain(resultsByRun => utils.getItem(resultsByRun.all, resultId));
    }, 'getResultById');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.getResultBySubmission = moduleBuilder.read(state => {
        const getter = utils.getItem(state.resultByRun);
        return (runId, submissionId) => getter(runId).chain(resultsByRun => utils
            .getItem(resultsByRun.bySubmission, submissionId)
            .chain(utils.getItem(resultsByRun.all)));
    }, 'getResultBySubmission');
    const commitAddLoader = moduleBuilder.commit((state, 
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    data) => {
        const obj = state.loaders[data.loader];
        utils.vueSet(obj, data.id, data.value);
    }, 'commitAddLoader');
    const commitRemoveLoader = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, data) => {
        const obj = state.loaders[data.loader];
        utils.vueDelete(obj, data.id);
    }, 'commitRemoveLoader');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    function storeLoader(data) {
        const loaders = data.state.loaders[data.loaderKey];
        return {
            loader: utils.getItem(loaders, data.id),
            addLoader: (value) => commitAddLoader({ loader: data.loaderKey, id: data.id, value }),
            removeLoader: () => commitRemoveLoader({ loader: data.loaderKey, id: data.id }),
        };
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    const deleteAutoTestFromState = (state, autoTest) => {
        utils.vueDelete(state.tests, autoTest.id);
        utils.vueDelete(state.testsByAssignment, autoTest.assignmentId);
        autoTest.runs.forEach(run => {
            utils.vueDelete(state.runs, run.id);
            utils.vueDelete(state.resultByRun, run.id);
        });
    };
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    function createAutoTestResult(state, { result, test, runId, }) {
        const maybeOldObj = utils
            .getItem(state.resultByRun, runId)
            .chain(resultState => utils.getItem(resultState.all, result.id));
        const newFromServer = models.AutoTestResult.fromServerData(result, test.id, runId);
        const newObj = maybeOldObj.caseOf({
            Just: oldObj => newFromServer.amend(oldObj),
            Nothing: () => newFromServer,
        });
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        const loadSubmission = (force) => {
            SubmissionsStore.loadSingleSubmission({
                courseId: test.assignment.map(utils.attr('courseId')).extract(),
                assignmentId: test.assignmentId,
                submissionId: newObj.submissionId,
                force,
            }).run();
        };
        maybeOldObj.ifJust(oldObj => {
            if (newObj.finished && !oldObj.finished) {
                loadSubmission(true);
            }
        });
        newObj.maybeSubmission.ifNothing(() => loadSubmission(false));
        // Update the AT file tree if we got an extended result and if we
        // retrieved new suites with files.
        if (result instanceof api.ExtendedAutoTestResult) {
            const oldSuitesWithFiles = maybeOldObj.chain(utils.attr('suitesWithFiles'));
            const newSuiteFiles = newObj.suitesWithFiles.orDefaultLazy(() => new Set());
            if (oldSuitesWithFiles
                .map(utils.setEq(newSuiteFiles))
                .map(utils.not)
                .orDefault(newSuiteFiles.size > 0)) {
                FileTreeStore.updateAutoTestTree({
                    assignmentId: test.assignmentId,
                    submissionId: newObj.submissionId,
                    autoTest: test,
                    autoTestTree: result.suiteFiles,
                });
            }
        }
        return newObj;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    const addAutoTestRunToState = (state, test, run) => {
        const runModel = models.AutoTestRun.fromServerData(run, test.id);
        utils.vueSet(state.runs, run.id, runModel);
        let newResults = [];
        if (run instanceof api.ExtendedAutoTestRun) {
            newResults = run.results.map(result => createAutoTestResult(state, { result, test, runId: runModel.id }));
        }
        utils.vueSet(state.resultByRun, run.id, {
            all: utils.makeLookupMapping(newResults, 'id'),
            bySubmission: utils.mapToObject(newResults, res => [res.submissionId, res.id]),
        });
        return runModel;
    };
    const commitAutoTest = moduleBuilder.commit((state, data) => {
        const autoTestModel = data.autoTestModel ?? models.AutoTest.fromServerData(data.data);
        deleteAutoTestFromState(state, autoTestModel);
        utils.vueSet(state.tests, autoTestModel.id, autoTestModel);
        utils.vueSet(state.testsByAssignment, autoTestModel.assignmentId, autoTestModel);
        if (data.data != null) {
            data.data.runs.forEach(run => {
                addAutoTestRunToState(state, autoTestModel, run);
            });
        }
    }, 'commitAutoTest');
    const commitAutoTestRun = moduleBuilder.commit((state, { autoTest, run, }) => {
        const runModel = addAutoTestRunToState(state, autoTest, run);
        const test = autoTest.addRun(runModel);
        utils.vueSet(state.tests, autoTest.id, test);
        utils.vueSet(state.testsByAssignment, autoTest.assignmentId, test);
    }, 'commitAutoTestRun');
    const commitDeleteAutoTest = moduleBuilder.commit(deleteAutoTestFromState, 'commitDeleteAutoTest');
    const commitAddResults = moduleBuilder.commit((state, { run, results, }) => {
        utils.getItem(state.resultByRun, run.id).ifJust(resultsByRun => {
            results.forEach(result => {
                utils.vueSet(resultsByRun.all, result.id, result);
                utils.vueSet(resultsByRun.bySubmission, result.submissionId, result.id);
            });
        });
    }, 'commitAddResults');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.commitClearAutoTests = moduleBuilder.commit(state => {
        Object.assign(state, makeInitialState());
    }, 'commitClearAutoTests');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.deleteAutoTest = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (_, { autoTest }) => client.autoTest
        .delete({ autoTestId: autoTest.id })
        .chain(response => utils.eitherAsyncUtils
        .all([
        forceLoadSubs(autoTest.assignmentId),
        forceLoadRubric(autoTest.assignmentId),
    ])
        .map(() => response))
        .map(response => response.addCallbacks({
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        onAfterSuccess() {
            commitDeleteAutoTest(autoTest);
            AssignmentsStore.updateAssignment({
                assignmentId: autoTest.assignmentId,
                assignmentProps: { auto_test_id: null },
            });
        },
    })), 'deleteAutoTest');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.updateAutoTest = moduleBuilder.dispatch((_, data) => client.autoTest
        .patch({
        autoTestId: data.autoTestId,
        requestBody: data.autoTestProps,
    })
        .ifRight(res => commitAutoTest({ data: res.body })), 'updateAutoTest');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.loadAutoTestByAssignment = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    ({ state }, { assignmentId, force = false }) => utils.loadApiWithMaybeForce({
        ...storeLoader({
            id: assignmentId,
            loaderKey: 'testsByAssignmentId',
            state,
        }),
        value: _getTestByAssignment(state)(assignmentId),
        force,
        load: () => client.assignment
            .getAutoTest({ assignmentId })
            .map(res => res.map(body => models.AutoTest.fromServerData(body)))
            .ifRight(res => commitAutoTest({ data: res.body, autoTestModel: res.cgResult })),
    }), 'loadAutoTestByAssignment');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.loadAutoTest = moduleBuilder.dispatch(({ state }, { autoTestId, assignmentId, force = false, }) => utils.loadApiWithMaybeForce({
        ...storeLoader({
            id: assignmentId,
            loaderKey: 'testsByAssignmentId',
            state,
        }),
        value: _getTest(state)(autoTestId),
        force,
        load: () => client.autoTest
            .get({
            autoTestId,
            latestOnly: true,
        })
            .map(res => res.map(body => models.AutoTest.fromServerData(body)))
            .ifRight(res => commitAutoTest({ data: res.body, autoTestModel: res.cgResult })),
    }), 'loadAutoTest');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    const reloadAutoTestAndRubric = (autoTest) => utils.eitherAsyncUtils.all([
        AutoTestStore.loadAutoTest({
            autoTestId: autoTest.id,
            assignmentId: autoTest.assignmentId,
            force: true,
        }),
        forceLoadRubric(autoTest.assignmentId),
    ]);
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.createAutoTestRun = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (_, data) => client.autoTest
        .startRun({
        autoTestId: data.autoTest.id,
    })
        .ifRight(({ body }) => {
        if (body instanceof api.AutoTestRun) {
            commitAutoTestRun({ autoTest: data.autoTest, run: body });
        }
    }), 'createAutoTestRun');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.loadAutoTestRun = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (ctx, { autoTest, force = false }) => {
        const oldRun = autoTest.run
            .toEither(new Error('AutoTest has not been run yet.'))
            .unsafeCoerce();
        return utils.loadApiWithMaybeForce({
            ...storeLoader({
                state: ctx.state,
                loaderKey: 'runs',
                id: oldRun.id,
            }),
            value: Maybe.fromPredicate(utils.attr('isExtended'), oldRun),
            force,
            load: () => client.autoTest
                .getRun({
                autoTestId: autoTest.id,
                runId: oldRun.id,
                latestOnly: true,
            })
                .map(response => {
                const augmentedResponse = response.map(body => {
                    commitAutoTestRun({ autoTest, run: response.body });
                    return _getRun(ctx.state)(body.id).unsafeCoerce();
                });
                return augmentedResponse;
            }),
        });
    }, 'loadAutoTestRun');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.createAutoTestSet = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (_, { autoTest }) => client.autoTest
        .addSet({
        autoTestId: autoTest.id,
    })
        .map(res => res.map(newSet => autoTest.addSet(newSet)))
        .ifRight(res => commitAutoTest({ autoTestModel: res.cgResult })), 'createAutoTestSet');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.deleteAutoTestSet = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (_, { autoTest, set }) => set
        .delete()
        .map(res => res
        .map(() => autoTest.removeSet(set))
        .addCallbacks({
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        onAfterSuccess() {
            commitAutoTest({ autoTestModel: this.cgResult });
        },
    }))
        .chain(deleteRes => forceLoadRubric(autoTest.assignmentId).map(reloadRes => [deleteRes, reloadRes])), 'deleteAutoTestSet');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.saveAutoTestSet = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (_, { autoTest, set }) => set
        .save()
        .map(res => res.map(newSet => autoTest.replaceSet(newSet)))
        .ifRight(res => commitAutoTest({ autoTestModel: res.cgResult }))
        .chain(saveRes => forceLoadRubric(autoTest.assignmentId).map(reloadRes => [saveRes, reloadRes])), 'saveAutoTestSet');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.deleteAutoTestSuite = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (_, { autoTest, suite }) => suite
        .delete()
        .chain(deleteRes => reloadAutoTestAndRubric(autoTest).map(loadRes => [...loadRes, deleteRes])), 'deleteAutoTestSuite');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.saveAutoTestSuite = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (_, { autoTest, suite }) => suite
        .save()
        .chain(saveRes => reloadAutoTestAndRubric(autoTest).map(loadRes => [...loadRes, saveRes])), 'saveAutoTestSuite');
    const loadAutoTestBySubmission = moduleBuilder.dispatch(({ state }, { autoTest, submission, force = true, }) => {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        function loadWithData(run) {
            return client.autoTest
                .getResultBySubmission({
                autoTestId: autoTest.id,
                runId: run.id,
                submissionId: submission.id,
            })
                .map(res => res.map(body => {
                const result = createAutoTestResult(state, {
                    result: body,
                    test: autoTest,
                    runId: run.id,
                });
                commitAddResults({ results: [result], run });
                return result;
            }));
        }
        return autoTest.run.caseOf({
            Nothing: () => utils.errorResponse('AutoTest has no run'),
            Just: run => {
                const value = AutoTestStore.getResultBySubmission()(run.id, submission.id);
                return utils.loadApiWithMaybeForce({
                    ...storeLoader({
                        state,
                        loaderKey: 'resultsBySubmissionId',
                        id: submission.id,
                    }),
                    value,
                    force,
                    load: () => loadWithData(run),
                });
            },
        });
    }, 'loadAutoTestBySubmission');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.loadAutoTestResult = moduleBuilder.dispatch(({ state }, data) => {
        const { autoTestId, submission, assignmentId, force = false } = data;
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        function loadWithData(test, run, result) {
            return client.autoTest
                .getResult({
                autoTestId: test.id,
                resultId: result.id,
                runId: result.runId,
            })
                .map(response => response.map(body => createAutoTestResult(state, {
                result: body,
                test,
                runId: result.runId,
            })))
                .ifRight(response => {
                commitAddResults({
                    results: [response.cgResult],
                    run,
                });
            });
        }
        const autoTestLoader = data.loadAutoTestByAssignment
            ? AutoTestStore.loadAutoTestByAssignment({ assignmentId, force: false })
            : AutoTestStore.loadAutoTest({ autoTestId, assignmentId, force: false });
        return autoTestLoader.chain(({ cgResult: autoTest }) => autoTest.run.caseOf({
            Nothing: () => utils.errorResponse('The AutoTest has not been run yet'),
            Just: run => loadAutoTestBySubmission({ autoTest, submission, force: false })
                .map(utils.attr('cgResult'))
                .chain(result => utils.loadApiWithMaybeForce({
                ...storeLoader({
                    state,
                    id: result.id,
                    loaderKey: 'results',
                }),
                force,
                value: Maybe.fromPredicate(utils.attr('isExtended'), result),
                load: () => loadWithData(autoTest, run, result),
            })),
        }));
    }, 'loadAutoTestResult');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.restartAutoTestResult = moduleBuilder.dispatch(({ state }, 
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    { autoTest, autoTestResultId }) => autoTest.run.caseOf({
        Nothing: () => EitherAsync.liftEither(Left(new Error('AutoTest has no run'))),
        Just: run => client.autoTest
            .restartResult({
            autoTestId: autoTest.id,
            runId: run.id,
            resultId: autoTestResultId,
        })
            .map(response => response.map(body => createAutoTestResult(state, {
            result: body,
            test: autoTest,
            runId: run.id,
        })))
            .ifRight(response => commitAddResults({
            run,
            results: [response.cgResult],
        })),
    }), 'restartAutoTestResult');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.clearAutoTestRun = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (_, { autoTest }) => {
        const loadAT = AutoTestStore.loadAutoTest({
            autoTestId: autoTest.id,
            assignmentId: autoTest.assignmentId,
            force: true,
        });
        // This also clears the rubricResults, so we don't need to do that
        // here too.
        const reloadSubs = forceLoadSubs(autoTest.assignmentId).chain(() => utils.emptyResponse());
        return utils.eitherAsyncUtils.all([loadAT, reloadSubs]);
    }, 'clearAutoTestRun');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.deleteAutoTestResults = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (_, { autoTest }) => {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        function after(response) {
            return AutoTestStore.clearAutoTestRun({ autoTest }).map(reloadRes => [response, ...reloadRes]);
        }
        return autoTest.run.caseOf({
            Nothing: () => eitherAsyncUtils.rightFromValue([
                api.ApiResult.fromResponseAndValue(new Response(), null),
            ]),
            Just: run => client.autoTest
                .stopRun({
                autoTestId: autoTest.id,
                runId: run.id,
            })
                .chainLeft(err => {
                if (err instanceof api.ApiResultError && err.response.status === 404) {
                    return utils.emptyResponse();
                }
                else {
                    return eitherAsyncUtils.leftFromValue(err);
                }
            })
                .chain(response => after(Either.of(response))),
        });
    }, 'deleteAutoTestResults');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.toggleFixture = moduleBuilder.dispatch((_, 
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    { autoTest, fixture }) => client.autoTest[fixture.hidden ? 'showFixture' : 'hideFixture']({
        autoTestId: autoTest.id,
        fixtureId: parseInt(fixture.id, 10),
    }).ifRight(() => {
        const newAutoTest = autoTest.updateFixtures([
            new models.AutoTestFixture(fixture.id, fixture.name, !fixture.hidden),
        ]);
        commitAutoTest({ autoTestModel: newAutoTest });
    }), 'toggleFixture');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.createAutoTest = moduleBuilder.dispatch(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (_, { assignment }) => client.autoTest
        .create({
        requestBody: {
            json: {
                assignmentId: assignment.id,
                resultsAlwaysVisible: Maybe.of(true),
                preferTeacherRevision: Maybe.of(false),
                gradeCalculation: 'full',
                // Disable if the setting is disabled so
                // when the setting is changed the
                // AutoTest doesn't break.
                enableCaching: SiteSettingsStore.getSetting()('AT_IMAGE_CACHING_ENABLED', assignment.maybeCourse.chain(utils.attr('tenantId'))),
            },
        },
    })
        .map(response => response.addCallbacks({
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        onAfterSuccess() {
            commitAutoTest({ data: this.body });
            return AssignmentsStore.updateAssignment({
                assignmentId: this.body.assignmentId,
                assignmentProps: {
                    auto_test_id: this.body.id,
                },
            });
        },
    })), 'createAutoTest');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    AutoTestStore.importAutoTest = moduleBuilder.dispatch((_, { srcAssignment, dstAssignment, }) => srcAssignment.autoTestId.caseOf({
        Nothing: () => utils.errorResponse('Assignment has no AutoTest'),
        Just: srcAutoTestId => client.autoTest
            .copy({
            autoTestId: srcAutoTestId,
            requestBody: {
                assignmentId: dstAssignment.id,
            },
        })
            .chain(response => utils.eitherAsyncUtils.all([
            eitherAsyncUtils.rightFromValue(response.addCallbacks({
                // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
                onAfterSuccess() {
                    return Promise.all([
                        AssignmentsStore.updateAssignment({
                            assignmentId: this.body.assignmentId,
                            assignmentProps: {
                                auto_test_id: this.body.id,
                            },
                        }),
                        commitAutoTest({ data: this.body }),
                    ]);
                },
            })),
            forceLoadSubs(dstAssignment.id),
            forceLoadRubric(dstAssignment.id),
        ])),
    }), 'importAutoTest');
    utils.signal.AFTER_USER_LOGGED_OUT.register(() => {
        AutoTestStore.commitClearAutoTests();
    });
})(AutoTestStore || (AutoTestStore = {}));
